import { useJump } from '@/components/MagicJump';
import Image from '@/components/Image';
import { useValidExposure } from '@/hooks/useValidExposure';
import type { EProductImageType } from '@/utils/enums/goods';
import { StarFilled } from '@zeelool/icon';
import { useId, type CSSProperties, type MouseEvent } from 'react';
import { MarketingProductHoverMedia } from './MarketingProductHoverMedia';

export interface MarketingRankingProductCardProps {
  href?: string;
  image?: string;
  hoverImage?: string;
  hoverImageType?: EProductImageType;
  hoverImageIsGray?: boolean;
  imageAlt: string;
  name: string;
  priceText: string;
  originalPriceText?: string;
  rank: number;
  rankText: string;
  soldText: string;
  ratingText?: string;
  isLarge: boolean;
  isWap: boolean;
  showRankingMeta?: boolean;
  contentClassName?: string;
  priceRowClassName?: string;
  priceTextClassName?: string;
  originalPriceTextClassName?: string;
  isSoldOut?: boolean;
  soldOutText?: string;
  width?: CSSProperties['width'];
  minWidth?: CSSProperties['minWidth'];
  height?: CSSProperties['height'];
  imageAreaHeight?: CSSProperties['height'];
  imageLayout?: 'ranking-top' | 'ranking-normal';
  onClick?: () => void;
  onExposure?: () => void;
}

function getRankingTopStyle(rank: number) {
  if (rank === 1) {
    return {
      from: '#FFE2AB',
      to: '#E9BB61',
    };
  }
  if (rank === 2) {
    return {
      from: '#C2D0D7',
      to: '#DADADA',
    };
  }
  if (rank === 3) {
    return {
      from: '#f2b3a3',
      to: '#f2b3a3',
    };
  }
  return {
    from: '#d9d1ff',
    to: '#d9d1ff',
  };
}

function RankingTopBadge({
  rank,
  rankText,
  className,
  isWap,
}: {
  rank: number;
  rankText: string;
  className: string;
  isWap: boolean;
}) {
  const badgeStyle = getRankingTopStyle(rank);
  const gradientId = useId();
  const isPcTopThree = !isWap && rank >= 1 && rank <= 3;
  const width = isPcTopThree ? 50 : 36;
  const bodyHeight = isPcTopThree ? 20 : 15;
  const triangleHeight = isPcTopThree ? 8 : 5;
  const height = bodyHeight + triangleHeight;
  const radius = 4;
  const textSize = isPcTopThree ? 14 : 11;
  const textTop = 2;

  return (
    <span className={`absolute z-[2] block ${className}`} style={{ width, height }} aria-hidden>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none">
        <defs>
          <linearGradient
            id={gradientId}
            x1={width / 2}
            y1="0"
            x2={width / 2}
            y2={height}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={badgeStyle.from} />
            <stop offset="100%" stopColor={badgeStyle.to} />
          </linearGradient>
        </defs>
        <path
          d={`M${radius} 0H${width - radius}C${width - radius / 2} 0 ${width} ${radius / 2} ${width} ${radius}V${bodyHeight}L${width / 2} ${height}L0 ${bodyHeight}V${radius}C0 ${radius / 2} ${radius / 2} 0 ${radius} 0Z`}
          fill={`url(#${gradientId})`}
        />
      </svg>
      <span
        className="absolute left-0 w-full text-center font-bb font-bold leading-normal text-[#282828]"
        style={{ top: textTop, fontSize: textSize }}
      >
        {rankText}
      </span>
    </span>
  );
}

const SOLD_OUT_BADGE_CLASS_NAME =
  'pointer-events-none absolute left-1/2 top-1/2 z-[2] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-center';
const SOLD_OUT_BADGE_TEXT_CLASS_NAME =
  'whitespace-pre-wrap text-[clamp(12px,0.885vw,17px)] leading-[clamp(14px,1.042vw,20px)] text-white';
export const MARKETING_PC_PRODUCT_CONTENT_CLASS_NAME =
  'box-border min-h-0 flex-1 px-[12px] pb-[16px] pt-[6px] text-center';
export const MARKETING_PC_TOP_PRODUCT_CONTENT_CLASS_NAME =
  'box-border min-h-0 flex-1 px-[12px] pb-[16px] pt-[24px] text-center';
export const MARKETING_PC_PRODUCT_IMAGE_AREA_HEIGHT = 'calc(100% - 66px)';
export const MARKETING_PC_TOP_PRODUCT_IMAGE_AREA_HEIGHT = 'calc(100% - 84px)';
const MARKETING_PC_PRODUCT_IMAGE_AREA_CLASS_NAME =
  'group/marketing-product-image relative box-border flex shrink-0 items-center justify-center overflow-hidden';
const MARKETING_PC_PRODUCT_IMAGE_BOX_CLASS_NAME = 'relative aspect-[2/1] h-auto w-[80%]';
const MARKETING_PC_TOP_PRODUCT_IMAGE_BOX_CLASS_NAME =
  'relative aspect-[2/1] h-auto w-[60%] min-[1025px]:w-[68%]';
const MARKETING_WAP_GRID_PRODUCT_IMAGE_BOX_CLASS_NAME =
  'relative aspect-[2/1] h-auto w-full max-w-[156px]';
const MARKETING_WAP_RANKING_PRODUCT_IMAGE_BOX_CLASS_NAME =
  'relative aspect-[2/1] h-auto w-[144px] shrink-0';

function getPcImageSizes(imageLayout: MarketingRankingProductCardProps['imageLayout']) {
  return imageLayout === 'ranking-top' ? '28vw' : '18vw';
}

export function MarketingRankingProductCard({
  href,
  image,
  hoverImage,
  hoverImageType,
  hoverImageIsGray,
  imageAlt,
  name,
  priceText,
  originalPriceText,
  rank,
  rankText,
  soldText,
  ratingText,
  isLarge,
  isWap,
  showRankingMeta = true,
  contentClassName,
  priceRowClassName,
  priceTextClassName,
  originalPriceTextClassName,
  isSoldOut = false,
  soldOutText,
  width,
  minWidth,
  height,
  imageAreaHeight,
  imageLayout = 'ranking-normal',
  onClick,
  onExposure,
}: MarketingRankingProductCardProps) {
  const { ref } = useValidExposure({
    callback: () => onExposure?.(),
    ts: href || `${imageAlt}-${rank}`,
  });
  const toJumpTarget = useJump();
  const shouldShowOriginalPrice = Boolean(originalPriceText && originalPriceText !== priceText);
  const rankingMetaText = ratingText || soldText;
  const pcImageSizes = getPcImageSizes(imageLayout);
  const pcProductImageBoxClassName =
    imageLayout === 'ranking-top'
      ? MARKETING_PC_TOP_PRODUCT_IMAGE_BOX_CLASS_NAME
      : MARKETING_PC_PRODUCT_IMAGE_BOX_CLASS_NAME;
  const defaultHeight = isWap
    ? showRankingMeta
      ? 96
      : 176
    : isLarge
      ? 326
      : showRankingMeta
        ? 310
        : 304;
  const cardStyle: CSSProperties = {
    height: height ?? defaultHeight,
    ...(width === undefined ? {} : { width }),
    ...(minWidth === undefined ? {} : { minWidth }),
  };
  const sizeClassName = width === undefined ? 'w-full flex-1' : 'flex-none';
  const cardClassName = `relative box-border flex min-w-0 overflow-hidden bg-white ${sizeClassName}`;
  const soldOutLabel = soldOutText || 'Sold\nOut';
  const renderRankingMetaText = () => (
    <>
      {ratingText && <StarFilled className="shrink-0 text-[14px] text-[#282828]" />}
      <span>{rankingMetaText}</span>
    </>
  );

  const handleCardClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.();

    if (
      !href ||
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    toJumpTarget({
      jump_type: 4,
      Jump_target: href,
    });
  };

  if (isWap && !showRankingMeta) {
    return (
      <a
        ref={ref}
        href={href || undefined}
        className={`${cardClassName} aspect-square flex-col rounded-[6px]`}
        style={cardStyle}
        aria-label={name}
        onClick={handleCardClick}
      >
        <div className="box-border flex h-[112px] shrink-0 items-center justify-center px-[12px] pt-[12px]">
          <div className={MARKETING_WAP_GRID_PRODUCT_IMAGE_BOX_CLASS_NAME}>
            {image && (
              <Image src={image} alt={imageAlt} fill sizes="45vw" className="object-contain" />
            )}
            {isSoldOut && (
              <span className={`${SOLD_OUT_BADGE_CLASS_NAME} size-[64px]`}>
                <span className={SOLD_OUT_BADGE_TEXT_CLASS_NAME}>{soldOutLabel}</span>
              </span>
            )}
          </div>
        </div>
        <div
          className={contentClassName || 'box-border min-h-0 flex-1 px-[8px] pb-[10px] text-center'}
        >
          <div className="truncate font-br text-[12px] font-normal leading-[14px] text-[#808080]">
            {name}
          </div>
          <div
            className={
              priceRowClassName || 'mt-[6px] flex items-baseline justify-center gap-[6px] font-br'
            }
          >
            <span
              className={
                priceTextClassName || 'font-bb text-[16px] font-bold leading-[19px] text-[#282828]'
              }
            >
              {priceText}
            </span>
            {shouldShowOriginalPrice && (
              <span
                className={
                  originalPriceTextClassName ||
                  'truncate text-[12px] font-normal leading-[14px] text-[#808080] line-through'
                }
              >
                {originalPriceText}
              </span>
            )}
          </div>
        </div>
        {isSoldOut && (
          <div className="pointer-events-none absolute inset-0 z-[1] rounded-[6px] bg-white/50" />
        )}
      </a>
    );
  }

  if (isWap) {
    return (
      <a
        ref={ref}
        href={href || undefined}
        className={`${cardClassName} rounded-[4px] px-[16px] py-[12px]`}
        style={cardStyle}
        aria-label={name}
        onClick={handleCardClick}
      >
        {showRankingMeta && (
          <>
            {rankText && (
              <RankingTopBadge
                rank={rank}
                rankText={rankText}
                className="left-[6px] top-[6px]"
                isWap
              />
            )}
            {rankingMetaText && (
              <span className="absolute right-0 top-0 z-[2] flex h-[20px] w-[160px] items-center justify-end gap-[8px] rounded-tr-[4px] bg-[linear-gradient(90deg,#FFF_0%,#FFF9D7_100%)] px-[6px] py-[3px] font-br text-[12px] font-normal leading-normal text-[#282828]">
                {renderRankingMetaText()}
              </span>
            )}
          </>
        )}
        <div className={MARKETING_WAP_RANKING_PRODUCT_IMAGE_BOX_CLASS_NAME}>
          {image && (
            <Image src={image} alt={imageAlt} fill sizes="144px" className="object-contain" />
          )}
          {isSoldOut && (
            <span className={`${SOLD_OUT_BADGE_CLASS_NAME} size-[64px]`}>
              <span className={SOLD_OUT_BADGE_TEXT_CLASS_NAME}>{soldOutLabel}</span>
            </span>
          )}
        </div>
        <div className="ml-[24px] flex h-[72px] min-w-0 flex-1 flex-col justify-center text-left">
          <div className="truncate font-br text-[12px] font-normal leading-normal text-[#808080]">
            {name}
          </div>
          <div
            className={
              priceRowClassName || 'mt-[14px] flex min-w-0 items-baseline gap-[6px] font-br'
            }
          >
            <span
              className={
                priceTextClassName || 'font-bb text-[16px] font-bold leading-normal text-[#282828]'
              }
            >
              {priceText}
            </span>
            {shouldShowOriginalPrice && (
              <span
                className={
                  originalPriceTextClassName ||
                  'truncate text-[12px] font-normal leading-normal text-[#808080] line-through'
                }
              >
                {originalPriceText}
              </span>
            )}
          </div>
        </div>
        {isSoldOut && (
          <div className="pointer-events-none absolute inset-0 z-[1] rounded-[4px] bg-white/50" />
        )}
      </a>
    );
  }

  return (
    <a
      ref={ref}
      href={href || undefined}
      className={`${cardClassName} flex-col rounded-[8px]`}
      style={cardStyle}
      aria-label={name}
      onClick={handleCardClick}
    >
      {showRankingMeta && (
        <>
          {rankText && (
            <RankingTopBadge
              rank={rank}
              rankText={rankText}
              className="left-[8px] top-[8px]"
              isWap={false}
            />
          )}
          {rankingMetaText && (
            <span className="absolute right-0 top-0 z-[2] flex h-[25px] w-[160px] items-center justify-end gap-[8px] bg-gradient-to-l from-[#fff7d7] to-transparent pr-[8px] font-br text-[12px] leading-[14px] text-[#282828]">
              {renderRankingMetaText()}
            </span>
          )}
        </>
      )}
      <div
        className={MARKETING_PC_PRODUCT_IMAGE_AREA_CLASS_NAME}
        style={imageAreaHeight === undefined ? undefined : { height: imageAreaHeight }}
      >
        <MarketingProductHoverMedia
          image={image}
          hoverImage={hoverImage}
          hoverImageType={hoverImageType}
          hoverImageIsGray={hoverImageIsGray}
          imageAlt={imageAlt}
          imageSizes={pcImageSizes}
          mainImageBoxClassName={pcProductImageBoxClassName}
          hoverProductImageBoxClassName={MARKETING_PC_PRODUCT_IMAGE_BOX_CLASS_NAME}
        />
        {isSoldOut && (
          <span className={`${SOLD_OUT_BADGE_CLASS_NAME} size-[110px]`}>
            <span className={SOLD_OUT_BADGE_TEXT_CLASS_NAME}>{soldOutLabel}</span>
          </span>
        )}
      </div>
      <div
        className={contentClassName || 'box-border min-h-0 flex-1 px-[12px] pb-[16px] text-center'}
      >
        <div className="truncate font-br text-[14px] font-normal leading-[17px] text-[#808080]">
          {name}
        </div>
        <div className="mt-[8px] flex items-baseline justify-center gap-[6px] font-br">
          <span className="font-bb text-[16px] font-bold leading-[19px] text-[#282828]">
            {priceText}
          </span>
          {shouldShowOriginalPrice && (
            <span
              className={
                originalPriceTextClassName ||
                'text-[14px] font-normal leading-[17px] text-[#808080] line-through'
              }
            >
              {originalPriceText}
            </span>
          )}
        </div>
      </div>
      {isSoldOut && (
        <div className="pointer-events-none absolute inset-0 z-[1] rounded-[8px] bg-white/50" />
      )}
    </a>
  );
}
